import { isHidden } from '@amazon/vinyl-tsx'
import type { ObservableValue } from '@amazon/vinyl-observable'
import { createDisposer, globalRef, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents, isTextEntry, tabbableQuery } from '@/util/interaction'

export interface DialogFocusOptions {
    /** Called for Escape; the dialog decides how (and whether) to close. */
    readonly close: () => void
    /** Initial focus; defaults to `[autofocus]`, then the first control not marked `data-dialog-dismiss`. */
    readonly initialFocus?: HTMLElement | null
}

const HEADING = 'h1, h2, h3, [role="heading"]'

const openDialogs = globalRef<{ readonly stack: object[] }>(() => ({ stack: [] }))

/** A key or focus aimed at a menu opened from the dialog (mounted outside it) is the menu's to handle. */
function inOwnMenu(dialog: HTMLElement, target: EventTarget | null): boolean {
    return (
        target instanceof Element && !dialog.contains(target) && !!target.closest('[role="menu"]')
    )
}

/** A radio group is one Tab stop: its checked radio, else its first. */
function isRadioTabStop(radio: HTMLInputElement, all: readonly HTMLElement[]): boolean {
    if (radio.checked || !radio.name) return true
    const group = all.filter(
        (el): el is HTMLInputElement =>
            el instanceof HTMLInputElement && el.type === 'radio' && el.name === radio.name
    )
    return !group.some((el) => el.checked) && group[0] === radio
}

/** `dialog`'s Tab stops in document order, skipping hidden controls. */
export function dialogTabStops(dialog: HTMLElement): HTMLElement[] {
    const all = Array.from(dialog.querySelectorAll<HTMLElement>(tabbableQuery)).filter(
        (el) => !isHidden(el)
    )
    return all.filter(
        (el) => !(el instanceof HTMLInputElement && el.type === 'radio') || isRadioTabStop(el, all)
    )
}

function autofocusStop(surface: HTMLElement): HTMLElement | undefined {
    return dialogTabStops(surface).find((el) => el.autofocus || el.hasAttribute('autofocus'))
}

/**
 * Where focus lands when a modal opens: its `[autofocus]` control, else the dialog itself, so a
 * screen reader reads its name first and the first Tab reaches the first control.
 */
export function modalOpeningFocus(dialog: HTMLElement): HTMLElement {
    return autofocusStop(dialog) ?? dialog
}

/** Where focus lands when `surface` opens: `[autofocus]`, else its first control not marked `data-dialog-dismiss`, else its first control, else itself. */
export function openingFocus(surface: HTMLElement): HTMLElement {
    const stops = dialogTabStops(surface)
    return (
        autofocusStop(surface) ??
        stops.find((el) => !el.hasAttribute('data-dialog-dismiss')) ??
        stops.at(0) ??
        surface
    )
}

/**
 * Non-modal focus for a surface shown and hidden in place (a docked panel):
 * each time `open$` turns true, focus moves from the control that opened it to
 * the surface's heading (focusable via `tabindex="-1"`), else to
 * {@link openingFocus}; when it turns false, focus stranded inside (or dropped
 * to the body as the surface hid) returns to that control. Focus already
 * inside, on the body (nothing was activated, e.g. a deep link) or in a text
 * field (typing in the chat composer opens chat) stays put.
 */
export function bindOpenFocus(surface: HTMLElement, open$: ObservableValue<boolean>): Unsubscribe {
    let opener: HTMLElement | null = null
    let wasOpen = false
    return open$.onData((open) => {
        if (open === wasOpen) return
        wasOpen = open
        const active = document.activeElement
        if (open) {
            opener =
                active instanceof HTMLElement &&
                active !== document.body &&
                !surface.contains(active) &&
                !isTextEntry(active)
                    ? active
                    : null
            if (!opener) return
            surface.querySelector<HTMLElement>(HEADING)?.focus()
            if (!surface.contains(document.activeElement)) openingFocus(surface).focus()
            return
        }
        const stranded = active === null || active === document.body || surface.contains(active)
        if (stranded && opener?.isConnected) opener.focus()
        opener = null
    })
}

const OVERLAY_LAYER =
    '[role="dialog"], [role="alertdialog"], [role="menu"], [role="status"], [role="alert"], [aria-live], .modalLayer'

/**
 * Makes everything outside `dialog` `inert` (unreachable by Tab, pointer and
 * screen-reader cursor) until the returned disposer runs. Dialogs, menus and
 * live regions stay reachable: the dialog can open them on top of itself.
 */
export function inertOutside(dialog: HTMLElement): Unsubscribe {
    const made: Element[] = []
    const inertAllBut = (keep: Element) => {
        for (const sibling of Array.from(keep.parentElement?.children ?? [])) {
            if (sibling !== keep) inertUnlessLayer(sibling)
        }
    }
    const inertUnlessLayer = (el: Element) => {
        if (el.hasAttribute('inert') || el.matches(OVERLAY_LAYER)) return
        if (el.querySelector(OVERLAY_LAYER)) {
            for (const child of Array.from(el.children)) inertUnlessLayer(child)
            return
        }
        el.setAttribute('inert', '')
        made.push(el)
    }
    for (
        let el: Element = dialog;
        el.parentElement && el !== document.body;
        el = el.parentElement
    ) {
        inertAllBut(el)
    }
    return () => {
        for (const el of made) el.removeAttribute('inert')
    }
}

/**
 * Modal focus for an open dialog: focus moves inside, Tab / Shift+Tab cycle
 * its controls, focus that strays outside is pulled back, and Escape calls
 * `close` (preventDefaulted, so app Back and TV spatial nav skip it). Bind on
 * open; the returned disposer, called on close, hands focus back to the opener.
 * Listeners sit at the document capture phase so the dialog wins Escape over
 * page-level shells (settings, chat history) that navigate on it. With dialogs
 * stacked, only the most recently opened one handles keys and focus.
 */
export function trapDialogFocus(dialog: HTMLElement, options: DialogFocusOptions): Unsubscribe {
    const { add, dispose } = createDisposer()
    const opener = document.activeElement as HTMLElement | null
    const token = {}
    const { stack } = openDialogs.value
    stack.push(token)
    add(() => {
        const index = stack.indexOf(token)
        if (index !== -1) stack.splice(index, 1)
    })
    const owns = (event: Event): boolean =>
        stack.at(-1) === token && !inOwnMenu(dialog, event.target)

    const firstStop = (): HTMLElement | undefined => dialogTabStops(dialog)[0]
    const initial = options.initialFocus ?? openingFocus(dialog)
    initial.focus({ preventScroll: true })

    add(
        documentEvents.on(
            'keydown',
            (event) => {
                if (!owns(event)) return
                if (event.key === 'Escape') {
                    event.preventDefault()
                    event.stopPropagation()
                    options.close()
                    return
                }
                if (event.key !== 'Tab') return
                event.preventDefault()
                const stops = dialogTabStops(dialog)
                if (stops.length === 0) {
                    dialog.focus()
                    return
                }
                const current = stops.indexOf(document.activeElement as HTMLElement)
                const last = stops.length - 1
                const next = event.shiftKey
                    ? current <= 0
                        ? last
                        : current - 1
                    : current === last
                      ? 0
                      : current + 1
                stops[next].focus()
            },
            { capture: true }
        )
    )

    add(
        documentEvents.on(
            'focus',
            (event) => {
                if (!owns(event) || dialog.contains(event.target as Node)) return
                event.stopPropagation()
                event.preventDefault()
                firstStop()?.focus()
            },
            { capture: true }
        )
    )

    add(() => {
        if (opener && document.contains(opener) && typeof opener.focus === 'function') {
            opener.focus()
        }
    })

    return dispose
}
